import { useLocation, useNavigate } from 'react-router-dom';
import { Header } from '../components/Header';
import { TmsTaskCardItem } from '../components/TmsTaskCardItem';
import { t } from '../../../i18n';
import { TmsPageState } from '../types/tms';
import { TmsWarehouseOperationTask } from '../../../graphql/queries/tmsTaskWarehouseOperationQueries';
import { TmsArrivalForm } from './components/TmsArrivalForm';
import { useTmsTaskOperations } from '../../tms/store/transportStore';
import { useSaveTmsTaskWaypointLocationInfo } from './hooks/useSaveTmsTaskWaypointLocationInfo';
import { fileUpload } from '../../../utils/storageUtils';
import { base64ToFile } from '../../../services/DeliveryDataService';
import { Dialog, Toast } from 'antd-mobile';
import { WaypointType, TmsOperationSourceEnum, TmsTaskWaypointNodeType } from '../../../enums/TmsEnum';
import { parseException } from '../../../utils/exceptionUtils';
import { FirstPhotoWatermarkLocation } from '../../../utils/appRuntime';

interface TaskData {
  taskId: string;
  routeName: string;
  waypointStatusId: number;
  waypointType: number;
  taskWaypointId: string;
  task?: TmsWarehouseOperationTask;
}

export default function TmsArrivalPage() {
  const location = useLocation();
  const { backRoute, ...taskData } = location.state as TmsPageState;
  const navigate = useNavigate();
  const { updateTmsTaskWaypointToArrived } = useTmsTaskOperations();
  const { save: saveTmsTaskWaypointLocationInfo } = useSaveTmsTaskWaypointLocationInfo();

  // 处理提交
  const handleSubmit = async (
    remark: string,
    photos: string[],
    sealPhotos: string[],
    firstPhotoLocation?: FirstPhotoWatermarkLocation | null
  ) => {
    if (!taskData) {
      Toast.show({
        content: t('pda.pages.tms.arrival.noTaskData') || 'No task data',
        position: 'center',
        duration: 2000
      });
      return;
    }

    const isOrigin = taskData.waypointType === WaypointType.ORIGIN;
    // 检查到达凭证照片数量：
    // 始发站：只要求车辆到达凭证；途径站/终到站：车辆到达凭证 + 封签照片
    if (photos.length < 1 || (!isOrigin && sealPhotos.length < 1)) {
      Toast.show({
        content: t('pda.pages.tms.arrival.photoDescription') || 'Please upload photos',
        position: 'center',
        duration: 2000
      });
      return;
    }

    try {
      // 上传到达凭证照片到S3
      const uploadPromises = photos.map(async (base64Photo) => {
        // 确保base64格式正确
        const formattedBase64 = base64Photo.startsWith('data:') ? base64Photo : `data:image/jpeg;base64,${base64Photo}`;
        // 转换为File对象
        const file = await base64ToFile(formattedBase64, `arrival_${Date.now()}_${Math.random().toString(36).substring(7)}.jpg`);
        // 上传到S3
        const result = await fileUpload(file, 'tms_arrival_photo');
        return result.objectKey;
      });

      // 上传到达封签照片到S3（如果有）
      const uploadSealPromises = sealPhotos.map(async (base64Photo) => {
        const formattedBase64 = base64Photo.startsWith('data:') ? base64Photo : `data:image/jpeg;base64,${base64Photo}`;
        const file = await base64ToFile(formattedBase64, `arrival_seal_${Date.now()}_${Math.random().toString(36).substring(7)}.jpg`);
        const result = await fileUpload(file, 'tms_arrival_seal_photo');
        return result.objectKey;
      });

      const [photosS3Keys, arrivalSealPhotosS3Keys] = await Promise.all([
        Promise.all(uploadPromises),
        Promise.all(uploadSealPromises)
      ]);
      console.log('Successfully uploaded arrival photos:', photosS3Keys, 'seal photos:', arrivalSealPhotosS3Keys);

      const taskId = parseInt(taskData.taskId);
      const taskWaypointId = parseInt(taskData.taskWaypointId);

      if (isNaN(taskId) || isNaN(taskWaypointId)) {
        throw new Error(t('pda.pages.tms.arrival.invalidTaskId') || 'Invalid task ID or waypoint ID');
      }

      await saveTmsTaskWaypointLocationInfo(taskId, taskWaypointId, TmsTaskWaypointNodeType.DRIVER_ARRIVAL, firstPhotoLocation);

      // 先执行乐观提交（仅更新本地状态）
      await updateTmsTaskWaypointToArrived({
        tmsTaskWayPointId: taskWaypointId,
        taskId: taskId,
        photosS3KeysJson: photosS3Keys,
        arrivalSealPhotosJson: arrivalSealPhotosS3Keys,
        arrivedRemark: remark || undefined
      }, true); // 乐观提交不传 operationSource

      // 真正的网络请求
      const success = await updateTmsTaskWaypointToArrived({
        tmsTaskWayPointId: taskWaypointId,
        taskId: taskId,
        photosS3KeysJson: photosS3Keys,
        arrivalSealPhotosJson: arrivalSealPhotosS3Keys,
        arrivedRemark: remark || undefined,
        operationSource: TmsOperationSourceEnum.PDA,
      }, false);

      if (success) {
        Toast.show({
          content: t('pda.common.submitSuccess') || 'Submit successful',
          position: 'center',
          duration: 2000
        });
        // 返回上一页：优先使用传入的 backRoute，否则回退一页
        if (backRoute) {
          navigate(backRoute);
        } else {
          navigate(-1);
        }
      } else {
        throw new Error(t('pda.common.submitFailed') || 'Submit failed');
      }
    } catch (error) {
      console.error('提交失败:', error);
      const { message } = parseException(error);
      const trimmed = message?.trim() ?? '';
      const dialogContent =
        trimmed.length > 0
          ? trimmed
          : t('pda.common.submitFailed') || 'Submit failed';
      Dialog.alert({
        content: dialogContent,
        confirmText: t('pda.common.confirm') || '确定',
      });
      throw error;
    }
  };

  return (
    <div className="w-full h-full flex flex-col gap-2 pt-2">
      <Header title={t('pda.pages.tms.arrival.label') || 'Arrival'} backRoute={backRoute} />
      <div className="p-3 bg-gray-50 flex flex-col gap-4 flex-1">
        {taskData?.task && <TmsTaskCardItem task={taskData.task} showButtons={false} />}
        <div className="bg-white rounded-lg p-4 shadow-sm">
          {taskData && (
            <TmsArrivalForm waypointType={taskData.waypointType} onSubmit={handleSubmit} />
          )}
        </div>
      </div>
    </div>
  );
}
